import { PitchShell } from "@/components/pitch-practice/PitchShell";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { format } from "date-fns";
import { Mic, ShieldCheck, TrendingUp, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GrantAvatar } from "@/components/pitch-practice/GrantPersona";
import { getSession } from "@/lib/pitch-practice/pitch.functions";
import { scoreTone } from "@/lib/pitch-practice/grant";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/accelerator/pitch-practice/scorecard/$id")({
  head: () => ({
    meta: [
      { title: "Scorecard — Grind Ventures Pitch Room" },
      { name: "description", content: "Grant Rogers' dual-use readiness and commercial viability scorecard for your pitch." },
      { property: "og:title", content: "Pitch Scorecard — Grind Ventures" },
      { property: "og:description", content: "Dual-use readiness and commercial viability scorecard." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (
    <PitchShell>
      <ScorecardPage />
    </PitchShell>
  ),
});

function Gauge({ label, value, icon: Icon }: { label: string; value: number; icon: typeof ShieldCheck }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <p className={cn("mt-2 font-display text-5xl font-extrabold", scoreTone(value))}>{value}</p>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function ScorecardPage() {
  const { id } = Route.useParams();
  const fetchSession = useServerFn(getSession);
  const q = useQuery({ queryKey: ["session", id], queryFn: () => fetchSession({ data: { id } }) });

  if (q.isLoading) return <div className="grid min-h-[70vh] place-items-center bg-background text-muted-foreground">Loading…</div>;
  const s = q.data;
  const card = s?.scorecard;
  if (q.error || !s || !card)
    return (
      <div className="grid min-h-[70vh] place-items-center bg-background text-center">
        <div>
          <p className="text-muted-foreground">{(q.error as Error)?.message ?? "No scorecard yet for this session."}</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/accelerator/pitch-practice">Back to my pitches</Link>
          </Button>
        </div>
      </div>
    );

  return (
    <div className="min-h-[70vh] bg-background">
      <main className="mx-auto max-w-5xl px-4 py-10">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-primary">Scorecard · {format(new Date(s.created_at), "MMM d, yyyy")}</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-extrabold">{s.startup_name}</h1>
            <p className="text-muted-foreground">{s.one_liner || s.stage}</p>
          </div>
          <span className="rounded border border-primary px-3 py-1.5 font-display font-bold text-primary">{card.verdict}</span>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-primary/40 bg-card p-5">
            <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Overall</span>
            <p className={cn("mt-2 font-display text-6xl font-extrabold", scoreTone(card.overall_score))}>
              {card.overall_score}
              <span className="text-xl text-muted-foreground">/100</span>
            </p>
          </div>
          <Gauge label="Dual-use readiness" value={card.dual_use_readiness} icon={ShieldCheck} />
          <Gauge label="Commercial viability" value={card.commercial_viability} icon={TrendingUp} />
        </div>

        <div className="mt-6 flex gap-4 rounded-lg border border-border bg-card p-5">
          <GrantAvatar className="h-12 w-12" />
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-primary">Grant's take</p>
            <p className="mt-1 leading-relaxed">{card.summary}</p>
          </div>
        </div>

        <h2 className="mt-10 text-2xl font-bold">Criteria</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {card.categories.map((c) => (
            <div key={c.key} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">{c.name}</h3>
                <span className={cn("font-display text-xl font-extrabold", scoreTone(c.score * 10))}>{c.score}/10</span>
              </div>
              <div className="mt-2 flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <span key={i} className={cn("h-1.5 flex-1 rounded-full", i < c.score ? "bg-primary" : "bg-muted")} />
                ))}
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{c.rationale}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {[
            { t: "Strengths", items: card.strengths, icon: CheckCircle2, tone: "text-success" },
            { t: "Red flags", items: card.red_flags, icon: AlertTriangle, tone: "text-destructive" },
            { t: "Fix before the real pitch", items: card.next_steps, icon: ArrowRight, tone: "text-primary" },
          ].map(({ t, items, icon: Icon, tone }) => (
            <div key={t} className="rounded-lg border border-border bg-card p-5">
              <h3 className={cn("flex items-center gap-2 font-bold", tone)}>
                <Icon className="h-4 w-4" /> {t}
              </h3>
              <ul className="mt-3 space-y-2 text-sm">
                {items.map((x, i) => (
                  <li key={i} className="border-l-2 border-border pl-3">
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <details className="mt-10 rounded-lg border border-border bg-card p-5">
          <summary className="cursor-pointer font-bold">Full transcript</summary>
          <div className="mt-4 space-y-3 text-sm">
            {s.transcript.map((t, i) => (
              <p key={i}>
                <span className={cn("text-[0.7rem] font-semibold uppercase tracking-[0.14em] mr-2", t.role === "grant" ? "text-primary" : "text-muted-foreground")}>
                  {t.role === "grant" ? "Grant" : "You"}
                </span>
                {t.text}
              </p>
            ))}
          </div>
        </details>

        <div className="mt-10 flex justify-center">
          <Button asChild size="lg">
            <Link to="/accelerator/pitch-practice">
              <Mic /> Pitch again
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
